
import { defineComponent, type PropType } from 'vue'
import { IdfIcon, IdfTag } from '@invivodf/ui-kit'

type Items = Record<string, string>

// `IdfTag` pilote ses couleurs par tokens ; le gris clair du design system n'a pas d'équivalent
// nominal, on prend le neutre le plus proche.
const TAG_BG_COLOR_TOKENS = { normal: '--idf-color-neutral-200' }

/**
 * Remplacement local d'`AdnItemsSelector` (@b2ag/design-system) : sélection multiple par menu
 * déroulant, chaque valeur retenue étant rendue en étiquette refermable.
 *
 * `IdfEnrichedSelect` (ui-kit 0.200) a été écarté : sa valeur est une chaîne unique, il ne
 * couvre ni la sélection multiple ni les étiquettes refermables.
 *
 * Comportement repris tel quel du design system : la sélection est un état interne, non piloté
 * par l'appelant — `CThresholdsCard` passait bien un `v-model`, mais `AdnItemsSelector` n'avait
 * ni prop `value` ni émission `input`, ce lien était donc sans effet.
 */
export default defineComponent({
  name: 'CItemsSelector',
  components: { IdfIcon, IdfTag },
  props: {
    items: {
      type: Object as PropType<Items>,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Sélectionner un élément',
    },
    noItemMessage: {
      type: String,
      default: 'Aucun élément restant',
    },
  },
  data() {
    return { selectedItems: {} as Items, showMenu: false, TAG_BG_COLOR_TOKENS }
  },
  computed: {
    hasSelectedItem(): boolean {
      return Object.keys(this.selectedItems).length > 0
    },
    selectableItems(): Items {
      return Object.keys(this.items)
        .filter((itemKey) => !(itemKey in this.selectedItems))
        .reduce((acc: Items, itemKey) => ({ ...acc, [itemKey]: this.items[itemKey] }), {})
    },
    hasSelectableItem(): boolean {
      return Object.keys(this.selectableItems).length > 0
    },
  },
  methods: {
    toggleMenu() {
      this.showMenu = !this.showMenu
    },
    select(itemKey: string) {
      this.$set(this.selectedItems, itemKey, this.items[itemKey])
      this.$emit('change', this.selectedItems)
    },
    unselect(itemKey: string) {
      this.$delete(this.selectedItems, itemKey)
      this.$emit('change', this.selectedItems)
    },
  },
})
